<template>
    <div class="animated fadeIn">
        <div v-if="isLoading" class="loading-container">
            <b-row class="justify-content-center">
                <b-col cols="12" class="text-center">
                    <atom-spinner
                        :animation-duration="1000"
                        :size="60"
                        color="#ff1d5e"
                        class="my-4"
                    />
                    <p class="loading-text">Loading subscriptions...</p>
                </b-col>
            </b-row>
        </div>
        <b-row class="mb-3 subscription-header-row" v-if="!isLoading">
            <b-col cols="12" sm="12" md="5" class="subscription-title-col"
                ><h3>Subscription</h3>
            </b-col>
            <b-col cols="12" sm="12" md="3" class="search-col">
                <div class="search-input-wrapper">
                    <b-form-input
                        class="searchField"
                        v-model="filter"
                        placeholder="Search plan name or amount"
                    />
                    <button
                        class="searchIcon"
                        type="button"
                        @click="filter ? clearSearch() : null"
                    >
                        <i :class="filter ? 'fa fa-times' : 'fa fa-search'"></i>
                    </button>
                </div>
            </b-col>
            <b-col cols="12" sm="12" md="2" class="create-btn-col">
                <b-button block variant="success" @click="handleSubscription"
                    >Create Subscription</b-button
                >
            </b-col>
            <b-col cols="12" sm="12" md="1" class="reload-btn-col">
                <b-button
                    size="sm"
                    variant="success"
                    @click="reloadData"
                    :disabled="isLoading"
                >
                    <span
                        v-if="isLoading"
                        class="fa fa-refresh fa-spin font-size-15"
                    />
                    <span v-else class="fa fa-refresh font-size-15" />
                </b-button>
            </b-col>
        </b-row>
        <b-card class="card-wrap" border-variant="info" v-if="!isLoading">
            <b-row>
                <b-col cols="12" sm="12" md="12">
                    <div class="table-responsive-wrapper">
                        <b-table
                            class="rounded-table tableRadius"
                            responsive
                            :dark="dark"
                            :hover="hover"
                            :striped="striped"
                            :bordered="bordered"
                            :small="small"
                            :fixed="fixed"
                            :items="subscriptionList"
                            :fields="fields"
                            :current-page="currentPage"
                            :filter="filter"
                            thead-class="header"
                            show-empty
                            :busy="isLoading"
                            :outlined="true"
                            :per-page="perPage"
                            @filtered="onFiltered"
                        >
                            <template slot="cell(actions)" slot-scope="data">
                                <div class="d-flex action-buttons-wrapper">
                                    <b-button
                                        class="mr-2 button-circle btn-warning"
                                        variant="primary"
                                        @click="
                                            editClick(
                                                data.item._id,
                                                data.item.planName,
                                                data.item.pricing.price,
                                                data.item.planDescription,
                                            )
                                        "
                                    >
                                        <i
                                            class="fa fa-pencil edit-pencil-icon"
                                        ></i>
                                    </b-button>
                                    <b-button
                                        class="button-circle"
                                        variant="danger"
                                        @click="deleteClick(data.item._id)"
                                    >
                                        <i class="fa fa-trash bin"></i>
                                    </b-button>
                                </div>
                            </template>

                            <template #row-details="row">
                                <b-card>
                                    <ul>
                                        <li
                                            v-for="(value, key) in row.item"
                                            :key="key"
                                        >
                                            <div class="mt-2">
                                                {{ key }}: {{ value }}
                                            </div>
                                        </li>
                                    </ul>
                                </b-card>
                            </template>
                        </b-table>
                    </div>
                </b-col>
            </b-row>
            <b-row>
                <b-col cols="12" sm="12" md="12">
                    <div class="pagination-wrapper">
                        <b-pagination
                            v-model="currentPage"
                            :total-rows="totalRows"
                            :per-page="perPage"
                            size="md"
                            class="my-0"
                            prev-text="Prev"
                            next-text="Next"
                            hide-goto-end-buttons
                        ></b-pagination>
                    </div>
                </b-col>
            </b-row>
        </b-card>
        <b-modal
            id="modalAdd"
            v-model="showModal"
            centered
            title="Add New Subscription"
            header-class="mb-3 header-text"
            footer-class="border-0 footer-button"
            modal-class="p-3"
            hide-footer
            ref="field-modal"
        >
            <ValidationObserver ref="observer">
                <b-row>
                    <b-col cols="12" sm="12" md="6" class="pb-0">
                        <label for=" Plan Name">
                            Plan Name <span class="text-danger">*</span></label
                        >
                        <ValidationProvider
                            rules="required"
                            v-slot="{ errors }"
                            name="Plan Name"
                            :key="resetKey"
                        >
                            <b-form-input
                                v-model="addPayload.planName"
                                placeholder="Enter Plan Name"
                                :class="{ 'is-invalid': errors[0] }"
                            ></b-form-input>
                            <span
                                v-if="errors[0]"
                                class="validate-error text-danger d-block mt-1"
                                >{{ errors[0] }}</span
                            >
                        </ValidationProvider>
                    </b-col>

                    <b-col cols="12" sm="12" md="6" class="pb-0">
                        <label for=" Plan Amount ">
                            Plan Amount
                            <span class="text-danger">*</span></label
                        >
                        <ValidationProvider
                            rules="required|numeric"
                            v-slot="{ errors }"
                            name="Plan Amount"
                            :key="resetKey"
                        >
                            <b-form-input
                                v-model="addPayload.pricing.price"
                                maxlength="6"
                                placeholder="Enter Plan Amount"
                                type="number"
                                :class="{ 'is-invalid': errors[0] }"
                            ></b-form-input>
                            <span
                                v-if="errors[0]"
                                class="validate-error text-danger d-block mt-1"
                                >{{ errors[0] }}</span
                            >
                        </ValidationProvider>
                    </b-col>
                    <b-col cols="12" sm="12" md="12" class="pb-0 mt-2">
                        <label for="Description"
                            >Description
                            <span class="text-danger">*</span></label
                        >
                        <ValidationProvider
                            rules="required"
                            v-slot="{ errors }"
                            name="Description"
                            :key="resetKey"
                        >
                            <b-form-input
                                v-model="addPayload.planDescription"
                                inputType="text"
                                maxlength="100"
                                placeholder="Enter Description"
                                :class="{ 'is-invalid': errors[0] }"
                            ></b-form-input>
                            <span
                                v-if="errors[0]"
                                class="validate-error text-danger d-block mt-1"
                                >{{ errors[0] }}</span
                            >
                        </ValidationProvider>
                    </b-col>
                </b-row>
                <b-row class="mt-4 mb-1 justify-content-center">
                    <b-col cols="12" sm="6" md="auto" class="subscription-cancel-btn-col text-center mb-2 mb-sm-0">
                        <b-button
                            class="btn-width-100"
                            block
                            variant="danger"
                            @click="CancelButton"
                            >CANCEL</b-button
                        >
                    </b-col>
                    <b-col cols="12" sm="6" md="auto" class="text-center">
                        <b-button
                            class="btn-width-100"
                            block
                            variant="primary"
                            @click="saveSubscription"
                            >SAVE</b-button
                        >
                    </b-col>
                </b-row>
            </ValidationObserver>
        </b-modal>

        <!--Update-->
        <b-modal
            id="modalUpdate"
            v-model="showModalEdit"
            centered
            title="Update Subscription"
            header-class="mb-3 header-text"
            footer-class="border-0 footer-button"
            modal-class="p-3"
            hide-footer
            ref="field-modal"
        >
            <b-row>
                <b-col cols="12" sm="12" md="6" class="pb-0">
                    <label for=" Plan Name ">
                        Plan Name <span class="text-danger">*</span>:</label
                    >
                    <b-form-input
                        v-model="updatePayload.planName"
                        placeholder="Enter Plan Name"
                        :class="{ 'is-invalid': !updatePayload.planName }"
                    ></b-form-input>
                    <span
                        v-if="!updatePayload.planName"
                        class="validate-error text-danger d-block mt-1"
                        >Plan Name is required</span
                    >
                </b-col>
                <b-col cols="12" sm="12" md="6" class="pb-0">
                    <label for=" Plan Amount ">
                        Plan Amount <span class="text-danger">*</span>:</label
                    >
                    <b-form-input
                        v-model="updatePayload.pricing.price"
                        placeholder="Enter Plan Amount"
                        maxlength="6"
                        type="number"
                        :class="{
                            'is-invalid':
                                isValidUpdateAmount ||
                                !updatePayload.pricing.price,
                        }"
                    ></b-form-input>
                    <span
                        v-if="isValidUpdateAmount"
                        class="validate-error text-danger d-block mt-1"
                    >
                        Amount should be Numeric
                    </span>
                    <span
                        v-else-if="!updatePayload.pricing.price"
                        class="validate-error text-danger d-block mt-1"
                    >
                        Plan Amount is required
                    </span>
                </b-col>
                <b-col cols="12" sm="12" md="12" class="mt-2 pb-0">
                    <label for="Description">
                        Description <span class="text-danger">*</span>:</label
                    >
                    <b-form-input
                        v-model="updatePayload.planDescription"
                        placeholder="Enter Description"
                        :class="{
                            'is-invalid': !updatePayload.planDescription,
                        }"
                    ></b-form-input>
                    <span
                        v-if="!updatePayload.planDescription"
                        class="validate-error text-danger d-block mt-1"
                        >Description is required</span
                    >
                </b-col>
            </b-row>
            <b-row class="mt-4 mb-1 justify-content-center">
                <b-col
                    cols="12"
                    sm="6"
                    md="auto"
                    class="subscription-cancel-btn-col text-center mb-2 mb-sm-0"
                >
                    <b-button
                        block
                        variant="danger"
                        @click="showModalEdit = false"
                        class="btn-width-100"
                    >
                        CANCEL
                    </b-button>
                </b-col>
                <b-col cols="12" sm="6" md="auto" class="text-center">
                    <b-button
                        block
                        class="btn-width-100"
                        variant="primary"
                        @click="updateSubscription"
                    >
                        Update
                    </b-button>
                </b-col>
            </b-row>
        </b-modal>
    </div>
</template>
<script>
import { mapState } from "vuex";
import currentUserData from "@/mixins/currentUser";
import ebButton from "@/components/FormElements/Button";
import { AtomSpinner } from "epic-spinners";
import { ValidationProvider, ValidationObserver } from "vee-validate";
export default {
    mixins: [currentUserData],
    components: {
        ebButton,
        AtomSpinner,
        ValidationProvider,
        ValidationObserver,
    },
    data() {
        return {
            resetKey: 0,
            hover: true,
            striped: false,
            bordered: true,
            small: false,
            fixed: false,
            dark: false,
            currentUserData: "",
            subsId: "",
            addPayload: {
                scope: "",
                planName: "",
                planDescription: "",
                planFeatures: ["feature a", "feature b", "feature c"],
                provisioning: {
                    planType: "DURATION",
                    validity: 1,
                },
                // planAccess: "",
                taxCode: "123456",
                chargeCode: "SMART_CHARGE",
                paymentType: "PRE-PAID",
                autoRenewal: true,
                pricing: {
                    cadence: "MONTHLY",
                    price: "",
                    currency: "INR",
                    uom: "OCCURRENCE",
                    baseQuantity: 1,
                },
                isActive: true,
            },
            updatePayload: {
                planName: "",
                planDescription: "",
                pricing: {
                    price: "",
                },
            },
            isLoading: false,
            filter: "",
            fields: [
                {
                    key: "planName",
                    label: "Plan Name",
                    sortable: true,
                    sortDirection: "desc",
                    tdClass: "pt-2 text-left",
                    thClass: "text-left",
                    thStyle: { width: "28%" },
                },
                {
                    key: "pricing.price",
                    label: "Plan Amount",
                    tdClass: "pt-2 text-left",
                    thClass: "text-left",
                },
                {
                    key: "planDescription",
                    label: "Plan Description",
                    formatter: "shortDescription",
                    class: "textAlignment",
                    tdClass: "text-left",
                    thClass: "text-left",
                },
                {
                    key: "actions",
                    label: "Action",
                    thStyle: { width: "8%" },
                    tdClass: "text-left",
                    thClass: "text-left",
                },
            ],

            showModal: false,
            showModalEdit: false,
            totalRows: 1,
            currentPage: 1,
            perPage: 10,
        };
    },
    computed: {
        ...mapState({
            subscriptionListRaw: (state) => state.subscription.subscriptionList,
        }),
        subscriptionList() {
            const list = this.subscriptionListRaw || [];
            return [...list].sort((a, b) => {
                const dateA = new Date(a.createdAt || a.updatedAt || 0);
                const dateB = new Date(b.createdAt || b.updatedAt || 0);
                return dateB - dateA;
            });
        },

        isValidAmount() {
            if (this.addPayload.pricing.price == "") return false;
            var isAmount = /^[0-9]+$/;
            if (isAmount.test(this.addPayload.pricing.price)) {
                return false;
            } else {
                return true;
            }
        },
        isValidUpdateAmount() {
            if (this.updatePayload.pricing.price == "") return false;
            var isAmount = /^[0-9]+$/;
            if (isAmount.test(this.updatePayload.pricing.price)) {
                return false;
            } else {
                return true;
            }
        },
    },
    methods: {
        clearSearch() {
            this.filter = "";
            this.currentPage = 1;
        },
        CancelButton() {
            this.showModal = false;
            this.addPayload.planName = "";
            this.addPayload.pricing.price = "";
            this.addPayload.planDescription = "";
        },
        handleSubscription() {
            this.showModal = true;
            this.resetKey++;
        },
        onFiltered(filteredItems) {
            // Trigger pagination to update the number of buttons/pages due to filtering
            this.totalRows = filteredItems.length;
            this.currentPage = 1;
        },
        shortDescription(value) {
            if (value.length > 100) {
                return value.slice(0, 100) + "" || "";
            }
            return value;
        },
        async getList() {
            this.isLoading = true;
            try {
                await this.$store.dispatch(
                    "subscription/getSubscriptionListAction",
                );
                this.totalRows = this.subscriptionList.length || 0;
            } catch (e) {
                console.error("Error fetching subscription list:", e);
                this.$toasted.error(
                    "Failed to load subscriptions. Please try again.",
                );
                this.totalRows = 0;
            } finally {
                this.isLoading = false;
            }
        },
        reloadData() {
            this.getList();
        },
        async saveSubscription() {
            const isValid = await this.$refs.observer.validate();
            if (!isValid) return;
            // this.addPayload.planAccess = this.$currentUserData._id;
            this.addPayload.scope = (this.$currentUserData.role || "").toUpperCase();

            this.$store
                .dispatch("subscription/addSubscriptionAction", this.addPayload)
                .then((result) => {
                    this.$swal({
                        title: "Success",
                        text: "Subscription Plan Added Successfully!!",
                        type: "success",
                    });
                    this.currentPage = 1;
                    this.filter = "";
                    this.reloadData();
                    this.showModal = false;
                    this.addPayload.planName = "";
                    this.addPayload.pricing.price = "";
                    this.addPayload.planDescription = "";
                })
                .catch((err) => {
                    console.error("Error adding subscription:", err);
                    const errorMessage =
                        err?.response?.data?.message ||
                        err?.message ||
                        "Failed to add subscription. Please check all required fields and try again.";
                    this.$swal({
                        title: "Error",
                        text: errorMessage,
                        type: "error",
                    });
                    this.showModal = true;
                });
        },
        editClick(id, name, price, description) {
            this.subsId = id;
            this.updatePayload.planName = name;
            this.updatePayload.pricing.price = price;
            this.updatePayload.planDescription =
                (description && description.slice(0, 60) + "") || "";
            this.showModalEdit = true;
        },
        deleteClick(id) {
            this.$swal
                .fire({
                    title: "Are you sure?",
                    text: "You won't be able to revert this!",
                    type: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#3085d6",
                    confirmButtonText: " Yes,Delete it!",
                    cancelButtonColor: "#d33",
                    reverseButtons: true,
                })
                .then((result) => {
                    if (result.value) {
                        this.deleteSubscription(id);
                    }
                });
        },
        deleteSubscription(id) {
            this.$store
                .dispatch("subscription/deleteSubscriptionAction", id)
                .then(() => {
                    this.$swal({
                        title: "Success",
                        text: "Subscription Plan Deleted Successfully!!",
                        type: "success",
                    });
                    this.reloadData();
                })
                .catch((err) => {
                    console.error("Error deleting subscription:", err);
                    const errorMessage =
                        err?.response?.data?.message ||
                        err?.message ||
                        "Failed to delete subscription. Please try again.";
                    this.$swal({
                        title: "Error",
                        text: errorMessage,
                        type: "error",
                    });
                });
        },
        updateSubscription() {
            // Validation
            if (
                !this.updatePayload.planName ||
                !this.updatePayload.pricing.price ||
                !this.updatePayload.planDescription
            ) {
                this.$swal({
                    title: "Validation Error",
                    text: "Please fill all required fields marked with (*).",
                    type: "error",
                });
                return;
            }

            if (this.isValidUpdateAmount) {
                this.$swal({
                    title: "Validation Error",
                    text: "Plan Amount must be a valid number.",
                    type: "error",
                });
                return;
            }

            let payload = {
                id: this.subsId,
                data: this.updatePayload,
            };
            this.$store
                .dispatch("subscription/updateSubscriptionAction", payload)
                .then(() => {
                    this.$swal({
                        title: "Success",
                        text: "Subscription Plan Updated Successfully!!",
                        type: "success",
                    });
                    this.reloadData();
                    this.showModalEdit = false;
                })
                .catch((err) => {
                    console.error("Error updating subscription:", err);
                    const errorMessage =
                        err?.response?.data?.message ||
                        err?.message ||
                        "Failed to update subscription. Please try again.";
                    this.$swal({
                        title: "Error",
                        text: errorMessage,
                        type: "error",
                    });
                });
        },
    },

    mounted() {
        this.getList();
    },
};
</script>

<style>
.rounded-table {
    border-radius: 12px;
}
.btn-warning {
    background: #fe7b08;
    border: #fe7b08;
}
.btn-warning:hover {
    background: #e66a00;
    border-color: #e66a00;
}

.edit-pencil-icon {
    color: white;
}
.font-size-15 {
    font-size: 15px;
}
.btn-width-100 {
    width: 100px;
}
@media (max-width: 767.98px) {
    .btn-width-100 {
        width: 100%;
        margin-left: 0 !important;
    }
    .ml-md-auto {
        margin-left: 0 !important;
    }
}
/* button.btn.button-circle {
    border-radius: 50px;
    height: 32px;
    width: 32px;
    text-align: center;
    line-height: 32px;
    padding: 0;
} */
.search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
}

.searchField {
    font-size: 12px;
    font-weight: 600;
    padding: 8px 40px 8px 12px;
    height: 38px;
    width: 100%;
    border-radius: 8px;
}

.searchIcon {
    position: absolute;
    right: 8px;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6c757d;
    z-index: 10;
}

.searchIcon:hover {
    color: #495057;
}

.searchIcon i {
    font-size: 14px;
}

/* Removed duplicate .center class */

.headclass td {
    border-top: 1px solid #f1f5f8;
}
.description {
    color: black;
}
.round {
    border-radius: 50%;
    height: 30px;
    width: 30px;
    padding: 0;
}
.nav-tabs .nav-link.active {
    color: var(--primary);
}
.modal-content {
    padding: 1rem;
}
select.form-control:not([size]):not([multiple]) {
    height: calc(3.2125rem - 6px);
}
.modal-title {
    margin-left: 0%;
}

tbody tr:nth-child(2n + 1) {
    border-bottom: 1px solid #e8e5e5;
}

td.textAlignment {
    text-align: left;
    white-space: pre-wrap;
}

/* Ensure all table headers and cells are left-aligned */
.rounded-table th,
.rounded-table td {
    text-align: left !important;
}

.rounded-table th.text-left,
.rounded-table td.text-left {
    text-align: left !important;
}

/* Action buttons wrapper - left align */
.action-buttons-wrapper {
    justify-content: flex-start !important;
    gap: 6px;
}

.action-buttons-wrapper .button-circle {
    flex-shrink: 0;
    margin-right: 0 !important;
    margin-left: 0 !important;
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    min-height: 32px !important;
    padding: 0 !important;
    border-radius: 50% !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    line-height: 1 !important;
}
.header-text {
    background-color: var(--secondary);
    text-align: center;
  
    border-radius: 10px 10px 0 0;
    padding-left: 1rem;
    padding-right: 1rem;
}

.header-text .modal-title {
    width: 100%;
    text-align: center;
    margin: 0 auto;
    color: #fff;
}

.header-text .close {
    color: #fff;
    opacity: 1;
    margin: 0;
    padding: 0.5rem;
}
.search-input-wrapper {
    width: 100%;
}

.searchField {
    font-size: 14px;
    font-weight: 500;
    padding-right: 40px;
    height: 38px;
}

.searchIcon {
    right: 8px;
}

/* Ensure buttons in the header align with the search box height */
.mb-3 .btn {
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Loading container */
.loading-container {
    min-height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
}

.loading-text {
    margin-top: 1rem;
    font-size: 1rem;
    color: var(--slate-600, #475569);
    font-weight: 500;
}

/* Responsive styles */
.subscription-header-row {
    align-items: center;
    justify-content: flex-start;
}

.subscription-header-row .subscription-title-col {
    margin-right: auto;
}

.subscription-header-row .search-col {
    padding-right: 0.35rem;
}

.subscription-header-row .create-btn-col {
    padding-left: 0.35rem;
    padding-right: 0.35rem;
}

.subscription-header-row .reload-btn-col {
    padding-left: 0.35rem;
}

.subscription-header-row .search-input-wrapper {
    max-width: 320px;
    width: 100%;
}

.subscription-header-row .searchIcon {
    top: 50%;
    transform: translateY(-45%);
    right: 0.5rem;
    font-weight: 300;
    padding: 0.25rem 0 0.25rem 0.25rem;
}

.subscription-header-row .searchIcon i {
    font-weight: 300;
}

.subscription-header-row .searchField {
    height: 40px;
    font-weight: 600;
}

.subscription-header-row .searchField::placeholder {
    font-size: 12px;
    font-weight: 400;
}

.subscription-header-row .create-btn-col .btn,
.subscription-header-row .reload-btn-col .btn {
    height: 38px;
}

.subscription-header-row h3 {
    margin-bottom: 0;
}

@media (max-width: 991.98px) {
    .subscription-header-row {
        flex-direction: column;
        align-items: stretch;
    }

    .subscription-header-row > [class*="col-"] {
        margin-bottom: 0.75rem;
        padding-left: 0;
        padding-right: 0;
    }

    .subscription-header-row h3 {
        margin-bottom: 0.5rem;
        text-align: left;
    }

    .subscription-header-row .search-input-wrapper {
        width: 100%;
        max-width: 100%;
        margin-bottom: 0.5rem;
    }

    .subscription-header-row .searchField {
        width: 100%;
        padding-right: 40px;
    }

    .subscription-header-row .searchIcon {
        position: absolute;
        right: 0.5rem;
        top: 50%;
        transform: translateY(-45%);
    }

    /* Ensure buttons are full width on tablet */
    .subscription-header-row .btn {
        width: 100%;
    }
}

@media (max-width: 767.98px) {
    .subscription-header-row {
        margin-bottom: 1rem;
    }

    .subscription-cancel-btn-col {
        margin-bottom: 10px;
    }

    .subscription-header-row .search-input-wrapper {
        width: 100%;
        max-width: 100%;
    }

    .subscription-header-row .searchField {
        width: 100%;
        padding-right: 44px;
    }

    .subscription-header-row .searchIcon {
        right: 0.5rem;
    }

    .card-wrap {
        margin: 0 -15px;
        border-radius: 0;
    }

    .table-responsive-wrapper {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        width: 100%;
        margin: 0 -15px;
        padding: 0 15px;
    }

    .rounded-table {
        font-size: 0.875rem;
        min-width: 600px; /* Ensure table doesn't get too compressed */
    }

    .rounded-table th,
    .rounded-table td {
        padding: 0.5rem 0.25rem;
        font-size: 0.8rem;
        text-align: left !important;
    }

    .pagination-wrapper {
        overflow-x: auto;
        width: 100%;
        justify-content: center !important;
        padding: 0.5rem 0;
    }

    .pagination-wrapper .pagination {
        flex-wrap: wrap;
        justify-content: center;
        margin: 0;
    }

    /* Improve table action buttons on mobile */
    .button-circle {
        width: 32px !important;
        height: 32px !important;
        padding: 0 !important;
    }

    .button-circle i {
        font-size: 12px !important;
    }

    .modal-body .row {
        margin-left: 0;
        margin-right: 0;
    }

    .modal-body [class*="col-"] {
        padding-left: 0.5rem;
        padding-right: 0.5rem;
    }

    /* Improve table action buttons on mobile */
    .button-circle {
        width: 32px !important;
        height: 32px !important;
        padding: 0 !important;
    }

    .button-circle i {
        font-size: 12px !important;
    }
}

@media (max-width: 576px) {
    .subscription-header-row h3 {
        font-size: 1.25rem;
        margin-bottom: 0.75rem;
    }

    .subscription-cancel-btn-col {
        margin-bottom: 10px;
    }

    .subscription-header-row .search-input-wrapper {
        width: 100%;
        max-width: 100%;
        margin-bottom: 0.5rem;
    }

    .subscription-header-row .searchField {
        font-size: 16px; /* Prevents zoom on iOS */
        width: 100%;
        padding-right: 44px;
    }

    .subscription-header-row .searchIcon {
        position: absolute;
        right: 0.5rem;
        top: 50%;
        transform: translateY(-45%);
    }

    .btn-width-100 {
        width: 100%;
        margin-left: 0 !important;
        margin-top: 0.5rem;
    }

    .modal-body .row .col-md-1 {
        display: none;
    }

    .modal-body .row [class*="col-"] {
        flex: 0 0 100%;
        max-width: 100%;
        margin-bottom: 0.75rem;
    }

    /* Better table display on very small screens */
    .rounded-table {
        font-size: 0.75rem;
    }

    .rounded-table th,
    .rounded-table td {
        padding: 0.4rem 0.2rem;
        white-space: nowrap;
        text-align: left !important;
    }

    .rounded-table th:first-child,
    .rounded-table td:first-child {
        position: sticky;
        left: 0;
        background-color: #fff;
        z-index: 1;
    }

    /* Pagination on mobile */
    .pagination-wrapper {
        padding: 1rem 0;
    }

    .pagination-wrapper .pagination .page-link {
        padding: 0.375rem 0.5rem;
        font-size: 0.875rem;
    }

    /* Action buttons spacing */
    .d-flex .button-circle {
        margin-right: 0.25rem !important;
    }
}

/* Error message styling */
.validate-error {
    font-size: 0.875rem;
    margin-top: 0.25rem;
}

.is-invalid {
    border-color: #dc3545;
}

.is-invalid:focus {
    border-color: #dc3545;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

.text-danger {
    font-weight: 600;
}
</style>
